/**
 * Editorial Engine — internal admin workspace for premium digital editions.
 * Route: /admin/editorial and /admin/editorial/materials/:materialId
 *
 * Sprint 01 foundation: structural editor + MASTER library + preview shell.
 * Deliberately not final visual design — that comes in Sprint 02.
 */
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Plus, Trash2, Copy, Eye, EyeOff, ArrowUp, ArrowDown, Monitor, Smartphone } from "lucide-react";
import { HeroEditor, HeroRender } from "@/components/editorial/HeroMaster";
import { EditorialStoryEditor, EditorialStoryRender } from "@/components/editorial/EditorialStoryMaster";
import { VisualStoryEditor, VisualStoryRender } from "@/components/editorial/VisualStoryMaster";
import { RecipeEditor, RecipeRender } from "@/components/editorial/RecipeMaster";
import { HistoricalEditorialEditor, HistoricalEditorialRender } from "@/components/editorial/HistoricalEditorialMaster";
import { MacroSensoryEditor, MacroSensoryRender } from "@/components/editorial/MacroSensoryMaster";
import { PortraitDialogueEditor, PortraitDialogueRender } from "@/components/editorial/PortraitDialogueMaster";
import { MacroEditor, MacroRender } from "@/components/editorial/MacroMaster";
import { useAuth } from "@/context/AuthContext";

const ED = "/admin/editorial";

// ---------- Editions list ----------
export function EditorialHome() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [editions, setEditions] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      // Load products only for admins — the picker is admin-only.
      // Editors still see the linked product NAME (read-only) via the
      // `products` list resolved here when available; if the request fails
      // (role check), the UI silently falls back to just the product_id.
      const requests = [
        api.get(`${ED}/editions`),
        api.get(`${ED}/volumes`),
        api.get(`${ED}/materials`),
      ];
      if (isAdmin) requests.push(api.get(`/admin/products`).catch(() => ({ data: [] })));
      const [ed, vol, mat, prd] = await Promise.all(requests);
      const volsByEd = {};
      (vol.data || []).forEach((v) => { (volsByEd[v.edition_id] ||= []).push(v); });
      const matsByVol = {};
      (mat.data || []).forEach((m) => { (matsByVol[m.volume_id] ||= []).push(m); });
      setEditions((ed.data || []).map((e) => ({
        ...e,
        volumes: (volsByEd[e.id] || []).map((v) => ({
          ...v, materials: matsByVol[v.id] || [],
        })),
      })));
      setProducts((prd?.data || []));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  // Lookup helper: resolve linked product name for a volume regardless of role.
  const productNameById = useMemo(() => {
    const m = new Map();
    products.forEach((p) => m.set(p.id, p.title || p.slug));
    return m;
  }, [products]);

  const setVolumeProduct = async (volumeId, productId) => {
    try {
      await api.patch(`${ED}/volumes/${volumeId}`, { product_id: productId || null });
      toast.success(productId ? "Продукт привязан" : "Ссылка на продукт снята");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const createEdition = async () => {
    const title = window.prompt("Название нового издания?");
    if (!title) return;
    try { await api.post(`${ED}/editions`, { title, status: "draft" }); toast.success("Издание создано"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const createVolume = async (edition_id) => {
    const title = window.prompt("Название тома?");
    if (!title) return;
    try {
      const { data: v } = await api.post(`${ED}/volumes`, { edition_id, title, status: "draft" });
      // Admins may immediately attach a product when creating the volume.
      if (isAdmin && products.length > 0) {
        const list = products.map((p, i) => `${i + 1}. ${p.title || p.slug}`).join("\n");
        const pick = window.prompt(
          `Привязать продукт к тому? Введите номер, ENTER — пропустить.\n\n${list}`
        );
        const idx = parseInt(pick, 10);
        if (!Number.isNaN(idx) && idx >= 1 && idx <= products.length) {
          try {
            await api.patch(`${ED}/volumes/${v.id}`, { product_id: products[idx - 1].id });
          } catch (e) { toast.error(formatApiError(e)); }
        }
      }
      toast.success("Том создан");
      load();
    }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const createMaterial = async (volume_id) => {
    const title = window.prompt("Название материала?");
    if (!title) return;
    try {
      const { data } = await api.post(`${ED}/materials`, { volume_id, title, status: "draft" });
      toast.success("Материал создан");
      nav(`/admin/editorial/materials/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="editorial-home">
      <header className="border-b border-espresso/10 bg-white/60 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center gap-3">
          <button onClick={() => nav("/admin")} className="p-2 -ml-2 rounded-full hover:bg-sand/50"
            data-testid="editorial-back-admin">
            <ChevronLeft className="w-5 h-5 text-espresso" />
          </button>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">Anokina Studio</div>
            <h1 className="font-serif text-2xl text-espresso">Digital Editions</h1>
          </div>
          <Button onClick={createEdition} data-testid="editorial-new-edition">
            <Plus className="w-4 h-4 mr-1" /> Новое издание
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-8 space-y-6">
        {loading && <div className="opacity-60 text-sm">Загрузка…</div>}
        {!loading && editions.length === 0 && (
          <div className="p-16 text-center opacity-70">
            <div className="font-serif text-2xl mb-2">Изданий пока нет</div>
            <div className="text-sm mb-6">Создайте первое цифровое издание, чтобы начать.</div>
            <Button onClick={createEdition}><Plus className="w-4 h-4 mr-1" /> Создать издание</Button>
          </div>
        )}
        {editions.map((e) => (
          <div key={e.id} className="rounded-2xl bg-white/60 border hairline p-5"
               data-testid={`editorial-edition-${e.id}`}>
            <div className="flex items-baseline justify-between gap-3 mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest opacity-60">Издание</div>
                <div className="font-serif text-2xl text-espresso">{e.title}</div>
                {e.subtitle && <div className="italic opacity-70 text-sm">{e.subtitle}</div>}
              </div>
              <Button variant="outline" size="sm" onClick={() => createVolume(e.id)}
                data-testid={`editorial-new-volume-${e.id}`}>
                <Plus className="w-4 h-4 mr-1" /> Том
              </Button>
            </div>
            {(e.volumes || []).length === 0 && (
              <div className="text-sm opacity-60 pl-2">Томов пока нет.</div>
            )}
            {(e.volumes || []).map((v) => (
              <div key={v.id} className="mt-3 pl-3 border-l-2 border-espresso/15"
                   data-testid={`editorial-volume-${v.id}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest opacity-60">Том</div>
                    <div className="font-serif text-xl text-espresso">{v.title}</div>
                    {/* Linked product — name shown to everyone; picker shown to admin only. */}
                    {isAdmin ? (
                      <div className="mt-1 flex items-center gap-2 text-xs" data-testid={`editorial-volume-product-picker-${v.id}`}>
                        <span className="opacity-60">Продукт:</span>
                        <select
                          value={v.product_id || ""}
                          onChange={(ev) => setVolumeProduct(v.id, ev.target.value || null)}
                          className="h-7 rounded border border-espresso/20 bg-white px-2"
                          data-testid={`editorial-volume-product-select-${v.id}`}
                        >
                          <option value="">— не привязан —</option>
                          {products.map((p) => (
                            <option key={p.id} value={p.id}>{p.title || p.slug}</option>
                          ))}
                        </select>
                      </div>
                    ) : v.product_id ? (
                      <div
                        className="mt-1 text-xs opacity-70"
                        data-testid={`editorial-volume-product-label-${v.id}`}
                      >
                        Продукт: {productNameById.get(v.product_id) || v.product_id}
                      </div>
                    ) : null}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => createMaterial(v.id)}
                    data-testid={`editorial-new-material-${v.id}`}>
                    <Plus className="w-4 h-4 mr-1" /> Материал
                  </Button>
                </div>
                {(v.materials || []).length === 0 && (
                  <div className="text-sm opacity-60 mt-2">Материалов пока нет.</div>
                )}
                <ul className="mt-2 space-y-1.5">
                  {(v.materials || []).map((m) => (
                    <li key={m.id}>
                      <button onClick={() => nav(`/admin/editorial/materials/${m.id}`)}
                        className="w-full text-left rounded-lg bg-white border hairline p-3 hover:bg-sand/30 flex items-center gap-3"
                        data-testid={`editorial-material-${m.id}`}>
                        <div className="flex-1">
                          <div className="font-serif text-lg text-espresso">{m.title}</div>
                          {m.subtitle && <div className="text-xs opacity-60">{m.subtitle}</div>}
                        </div>
                        <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded ${
                          m.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-neutral-200 text-neutral-600"
                        }`}>{m.status}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Material editor ----------
const MASTER_CACHE_KEY = "editorial_master_types_cache";

export function EditorialMaterialEditor() {
  const { materialId } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [material, setMaterial] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [masters, setMasters] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(MASTER_CACHE_KEY) || "[]"); }
    catch { return []; }
  });
  const [saveState, setSaveState] = useState("idle"); // idle|saving|saved|error
  const [openLibrary, setOpenLibrary] = useState(false);
  const [preview, setPreview] = useState(searchParams.get("preview") || null); // null|desktop|mobile
  const debounceRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [m, bs, mt] = await Promise.all([
        api.get(`${ED}/materials/${materialId}`),
        api.get(`${ED}/materials/${materialId}/blocks`),
        api.get(`${ED}/master-types`),
      ]);
      setMaterial(m.data);
      setBlocks(bs.data || []);
      setMasters(mt.data || []);
      try { sessionStorage.setItem(MASTER_CACHE_KEY, JSON.stringify(mt.data || [])); } catch {}
    } catch (e) { toast.error(formatApiError(e)); }
  }, [materialId]);
  useEffect(() => { load(); }, [load]);

  // Debounced autosave of material title / subtitle
  const scheduleMaterialSave = (patch) => {
    setMaterial((m) => ({ ...m, ...patch }));
    setSaveState("saving");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        await api.patch(`${ED}/materials/${materialId}`, patch);
        setSaveState("saved");
        setTimeout(() => setSaveState("idle"), 1500);
      } catch (e) { setSaveState("error"); toast.error(formatApiError(e)); }
    }, 700);
  };

  // Immediate block operations (each already atomic)
  const patchBlock = async (bid, patch) => {
    setBlocks((bs) => bs.map((b) => (b.id === bid ? { ...b, ...patch } : b)));
    setSaveState("saving");
    try {
      await api.patch(`${ED}/blocks/${bid}`, patch);
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 1200);
    } catch (e) { setSaveState("error"); toast.error(formatApiError(e)); }
  };
  const debouncedPatchBlock = (bid, patch) => {
    setBlocks((bs) => bs.map((b) => (b.id === bid ? { ...b, ...patch } : b)));
    setSaveState("saving");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => patchBlock(bid, patch), 700);
  };

  const addBlock = async (master_type) => {
    try {
      const { data } = await api.post(`${ED}/materials/${materialId}/blocks`, {
        material_id: materialId, master_type,
        content: { headline: "" }, is_visible: true,
      });
      setBlocks((bs) => [...bs, data]);
      setOpenLibrary(false);
      toast.success("Разворот добавлен");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const move = async (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= blocks.length) return;
    const list = blocks.slice();
    [list[idx], list[to]] = [list[to], list[idx]];
    // renumber sort_order
    const items = list.map((b, i) => ({ id: b.id, sort_order: i }));
    setBlocks(list.map((b, i) => ({ ...b, sort_order: i })));
    try {
      await api.post(`${ED}/materials/${materialId}/blocks/reorder`, { items });
      setSaveState("saved"); setTimeout(() => setSaveState("idle"), 1200);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const duplicate = async (bid) => {
    try {
      await api.post(`${ED}/blocks/${bid}/duplicate`);
      toast.success("Разворот продублирован");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (bid) => {
    if (!window.confirm("Удалить разворот безвозвратно?")) return;
    try {
      await api.delete(`${ED}/blocks/${bid}`);
      setBlocks((bs) => bs.filter((b) => b.id !== bid));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleVisible = (b) => patchBlock(b.id, { is_visible: !b.is_visible });

  const publish = async () => {
    if (!window.confirm("Опубликовать текущую версию материала?")) return;
    try {
      const { data } = await api.post(`${ED}/materials/${materialId}/publish`);
      setMaterial(data);
      toast.success("Опубликовано");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setPreviewMode = (mode) => {
    setPreview(mode);
    const next = new URLSearchParams(searchParams);
    if (mode) next.set("preview", mode); else next.delete("preview");
    setSearchParams(next, { replace: true });
  };

  if (!material) return <div className="min-h-screen bg-cream" data-testid="editorial-loading" />;

  const saveLabel = {
    idle: "", saving: "Сохранение…", saved: "Сохранено", error: "Ошибка сохранения",
  }[saveState];

  return (
    <div className="min-h-screen bg-cream" data-testid="editorial-material-editor">
      <header className="border-b border-espresso/10 bg-white/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center gap-3">
          <button onClick={() => nav("/admin/editorial")}
            className="p-2 -ml-2 rounded-full hover:bg-sand/50"
            data-testid="editorial-editor-back">
            <ChevronLeft className="w-5 h-5 text-espresso" />
          </button>
          <div className="flex-1 min-w-0">
            <Input value={material.title || ""}
              onChange={(e) => scheduleMaterialSave({ title: e.target.value })}
              className="font-serif text-xl border-none bg-transparent px-0"
              data-testid="editorial-material-title" />
            <div className="text-[10px] uppercase tracking-widest opacity-60 flex items-center gap-3">
              <span>Статус: {material.status}</span>
              {saveLabel && <span data-testid="editorial-save-state">· {saveLabel}</span>}
            </div>
          </div>
          <Button variant={preview === "desktop" ? "default" : "outline"} size="sm"
            onClick={() => setPreviewMode(preview === "desktop" ? null : "desktop")}
            data-testid="editorial-preview-desktop">
            <Monitor className="w-4 h-4 mr-1" /> Desktop
          </Button>
          <Button variant={preview === "mobile" ? "default" : "outline"} size="sm"
            onClick={() => setPreviewMode(preview === "mobile" ? null : "mobile")}
            data-testid="editorial-preview-mobile">
            <Smartphone className="w-4 h-4 mr-1" /> Mobile
          </Button>
          <Button size="sm" onClick={publish} data-testid="editorial-publish">
            Опубликовать
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-6">
        {!preview && (
          <BlocksList blocks={blocks} masters={masters}
            onEditBlock={(bid, patch) => debouncedPatchBlock(bid, patch)}
            onMove={move} onDuplicate={duplicate} onRemove={remove}
            onToggleVisible={toggleVisible} onAdd={() => setOpenLibrary(true)} />
        )}
        {preview && (
          <>
            <div className="mb-4">
              <button
                onClick={() => setPreviewMode(null)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-espresso/25 bg-white/70 text-sm text-espresso hover:bg-white"
                data-testid="editorial-preview-back">
                <ChevronLeft className="w-4 h-4" /> Вернуться к редактированию
              </button>
            </div>
            <PreviewShell blocks={blocks} material={material} masters={masters} mode={preview} />
          </>
        )}
      </div>

      {openLibrary && (
        <MasterLibrary masters={masters} onClose={() => setOpenLibrary(false)} onPick={addBlock} />
      )}
    </div>
  );
}

// ---------- Blocks list ----------
function BlocksList({ blocks, masters, onEditBlock, onMove, onDuplicate, onRemove, onToggleVisible, onAdd }) {
  const masterLabel = (key) => masters.find((m) => m.key === key)?.label || key;
  return (
    <div className="space-y-4" data-testid="editorial-blocks-list">
      {blocks.length === 0 && (
        <div className="p-10 text-center opacity-70 bg-white/50 rounded-2xl border hairline">
          <div className="font-serif text-2xl mb-2">Разворотов пока нет</div>
          <div className="text-sm mb-4">Добавьте первый разворот из библиотеки MASTER.</div>
          <Button onClick={onAdd} data-testid="editorial-empty-add">
            <Plus className="w-4 h-4 mr-1" /> Добавить разворот
          </Button>
        </div>
      )}
      {blocks.map((b, idx) => (
        <div key={b.id}
          className={`rounded-2xl bg-white border hairline p-4 ${b.is_visible ? "" : "opacity-60"}`}
          data-testid={`editorial-block-${b.id}`}>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="text-[10px] tracking-widest opacity-50 w-6">#{String(idx + 1).padStart(2, "0")}</span>
            <span className="px-2 py-0.5 rounded bg-espresso/8 text-[11px] uppercase tracking-widest">
              {masterLabel(b.master_type)}
            </span>
            {b.variant && <span className="text-[11px] opacity-60">{b.variant}</span>}
            {!b.is_visible && <span className="text-[10px] italic opacity-70">скрыт</span>}
            <div className="flex-1 min-w-[4px]" />
            <div className="flex items-center gap-1 flex-shrink-0" role="toolbar" aria-label="Действия с блоком">
            <button onClick={() => onMove(idx, -1)} className="p-1 opacity-60 hover:opacity-100"
              title="Выше" data-testid={`editorial-block-up-${b.id}`}>
              <ArrowUp className="w-4 h-4" />
            </button>
            <button onClick={() => onMove(idx, +1)} className="p-1 opacity-60 hover:opacity-100"
              title="Ниже" data-testid={`editorial-block-down-${b.id}`}>
              <ArrowDown className="w-4 h-4" />
            </button>
            <button onClick={() => onDuplicate(b.id)} className="p-1 opacity-60 hover:opacity-100"
              title="Дублировать" data-testid={`editorial-block-duplicate-${b.id}`}>
              <Copy className="w-4 h-4" />
            </button>
            <button onClick={() => onToggleVisible(b)} className="p-1 opacity-60 hover:opacity-100"
              title={b.is_visible ? "Скрыть" : "Показать"} data-testid={`editorial-block-visible-${b.id}`}>
              {b.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </button>
            <button onClick={() => onRemove(b.id)} className="p-1 opacity-60 hover:opacity-100 text-red-600"
              title="Удалить" data-testid={`editorial-block-remove-${b.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
            </div>
          </div>
          {b.master_type === "HERO"
            ? <HeroEditor block={b} onPatch={(patch) => onEditBlock(b.id, patch)} />
            : b.master_type === "EDITORIAL_STORY"
              ? <EditorialStoryEditor block={b} onPatch={(patch) => onEditBlock(b.id, patch)} />
              : b.master_type === "VISUAL_STORY"
                ? <VisualStoryEditor block={b} onPatch={(patch) => onEditBlock(b.id, patch)} />
                : b.master_type === "RECIPE"
                  ? <RecipeEditor block={b} onPatch={(patch) => onEditBlock(b.id, patch)} />
                  : b.master_type === "HISTORICAL_EDITORIAL"
                    ? <HistoricalEditorialEditor block={b} onPatch={(patch) => onEditBlock(b.id, patch)} />
                    : b.master_type === "MACRO_SENSORY"
                      ? <MacroSensoryEditor block={b} onPatch={(patch) => onEditBlock(b.id, patch)} />
                      : b.master_type === "PORTRAIT_DIALOGUE"
                        ? <PortraitDialogueEditor block={b} onPatch={(patch) => onEditBlock(b.id, patch)} />
                        : b.master_type === "MACRO"
                          ? <MacroEditor block={b} onPatch={(patch) => onEditBlock(b.id, patch)} />
                          : <BlockEditor block={b} onChange={(patch) => onEditBlock(b.id, patch)} />}
        </div>
      ))}      <button onClick={onAdd}
        className="w-full py-4 rounded-2xl border-2 border-dashed border-espresso/25 text-espresso hover:bg-white/40"
        data-testid="editorial-add-block">
        + Добавить разворот
      </button>
    </div>
  );
}

// ---------- Block editor (structured fields) ----------
function BlockEditor({ block, onChange }) {
  const c = block.content || {};
  const patchContent = (patch) => onChange({ content: { ...c, ...patch } });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div>
        <label className="block text-[10px] uppercase tracking-widest opacity-60 mb-1">Надзаголовок</label>
        <Input value={c.eyebrow || ""} onChange={(e) => patchContent({ eyebrow: e.target.value })} />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-widest opacity-60 mb-1">Заголовок</label>
        <Input value={c.headline || ""} onChange={(e) => patchContent({ headline: e.target.value })}
          data-testid={`editorial-field-headline-${block.id}`} />
      </div>
      <div className="md:col-span-2">
        <label className="block text-[10px] uppercase tracking-widest opacity-60 mb-1">Лид</label>
        <textarea value={c.lead || ""} rows={2}
          onChange={(e) => patchContent({ lead: e.target.value })}
          className="w-full border rounded px-2 py-1.5 text-sm italic" />
      </div>
      <div className="md:col-span-2">
        <label className="block text-[10px] uppercase tracking-widest opacity-60 mb-1">Основной текст</label>
        <textarea value={c.body || ""} rows={6}
          onChange={(e) => patchContent({ body: e.target.value })}
          className="w-full border rounded px-2 py-2 text-sm font-serif leading-relaxed"
          data-testid={`editorial-field-body-${block.id}`} />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-widest opacity-60 mb-1">Цитата</label>
        <Input value={c.quote || ""} onChange={(e) => patchContent({ quote: e.target.value })} />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-widest opacity-60 mb-1">Подпись</label>
        <Input value={c.caption || ""} onChange={(e) => patchContent({ caption: e.target.value })} />
      </div>
      <div className="md:col-span-2">
        <label className="block text-[10px] uppercase tracking-widest opacity-60 mb-1">Media IDs (через запятую) — из существующей Медиатеки</label>
        <Input value={(c.media_ids || []).join(", ")}
          onChange={(e) => patchContent({ media_ids: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
          placeholder="uuid-1, uuid-2" />
      </div>
    </div>
  );
}

// ---------- MASTER library modal ----------
function MasterLibrary({ masters, onClose, onPick }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="editorial-master-library">
      <div className="w-full max-w-4xl max-h-[85vh] overflow-y-auto bg-cream rounded-2xl p-6">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-60">Библиотека MASTER</div>
            <div className="font-serif text-2xl text-espresso">Выберите композицию разворота</div>
          </div>
          <button onClick={onClose} className="text-sm opacity-70 hover:opacity-100">Закрыть</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {masters.map((m) => (
            <button key={m.key} onClick={() => onPick(m.key)}
              className="text-left rounded-xl bg-white border hairline p-4 hover:bg-sand/30 transition"
              data-testid={`editorial-master-${m.key}`}>
              <MasterPlaceholder type={m.key} />
              <div className="font-serif text-lg text-espresso mt-3">{m.label}</div>
              <div className="text-[11px] opacity-70 mt-1">{m.hint}</div>
              <div className="text-[9px] uppercase tracking-widest opacity-40 mt-2 font-mono">{m.key}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// simple visual placeholder per MASTER type
function MasterPlaceholder({ type }) {
  const box = "bg-espresso/10 rounded";
  const line = "bg-espresso/15 rounded h-1.5";
  const cfg = {
    HERO:                 <><div className={`${box} h-16 mb-1`} /><div className={`${line} w-2/3`} /></>,
    EDITORIAL_STORY:      <><div className={`${line} w-1/2 mb-1`} /><div className={`${line} w-full mb-1`} /><div className={`${line} w-full mb-1`} /><div className={`${line} w-4/5`} /></>,
    VISUAL_STORY:         <div className="grid grid-cols-3 gap-1"><div className={`${box} h-10`} /><div className={`${box} h-10`} /><div className={`${box} h-10`} /></div>,
    MACRO:                <div className={`${box} h-16`} />,
    PROCESS_STORY:        <><div className={`${line} w-1/4 mb-1`} /><div className={`${line} w-full mb-1`} /><div className={`${box} h-8 mb-1`} /><div className={`${line} w-3/4`} /></>,
    STEP_BY_STEP:         <div className="space-y-1">{[1,2,3].map(i=>(<div key={i} className="flex gap-1"><div className={`${box} w-6 h-4`} /><div className={`${line} flex-1 mt-1.5`} /></div>))}</div>,
    EDITORIAL_PAUSE:      <div className="flex items-center justify-center h-16"><div className={`${line} w-1/2 italic`} /></div>,
    RECIPE:               <div className="grid grid-cols-[1fr_2fr] gap-1"><div className="space-y-1">{[1,2,3].map(i=>(<div key={i} className={`${line} w-full`} />))}</div><div className="space-y-1">{[1,2,3,4].map(i=>(<div key={i} className={`${line} w-full`} />))}</div></div>,
    KNOWLEDGE:            <div className="grid grid-cols-4 gap-0.5">{[...Array(8)].map((_,i)=>(<div key={i} className={`${box} h-3`} />))}</div>,
    HISTORICAL_EDITORIAL: <><div className={`${line} w-1/3 mb-1 italic`} /><div className={`${line} w-full mb-1`} /><div className={`${line} w-full mb-1`} /><div className={`${line} w-2/3`} /></>,
  };
  return <div className="h-20 p-2 bg-cream rounded">{cfg[type] || <div className={box + " h-16"} />}</div>;
}

// ---------- Preview shell ----------
function PreviewShell({ blocks, material, masters, mode }) {
  const visible = (blocks || []).filter((b) => b.is_visible);
  const masterLabel = (k) => masters.find((m) => m.key === k)?.label || k;
  return (
    <div className="flex justify-center py-4" data-testid={`editorial-preview-${mode}`}>
      <div
        className={`${mode === "mobile" ? "w-[393px]" : "w-full max-w-[1440px]"} bg-white border hairline rounded-2xl overflow-hidden`}
        style={{ minHeight: 600 }}>
        <div className="px-8 py-6 border-b border-espresso/10">
          <div className="text-[10px] uppercase tracking-widest opacity-60">Предпросмотр — {mode}</div>
        </div>
        <div className="divide-y divide-espresso/10">
          {visible.length === 0 && <div className="opacity-60 text-sm px-8 py-6">Нет видимых разворотов.</div>}
          {visible.map((b) => (
            b.master_type === "HERO" ? (
              <HeroRender key={b.id} block={b} mode={mode} />
            ) : b.master_type === "EDITORIAL_STORY" ? (
              <EditorialStoryRender key={b.id} block={b} mode={mode} />
            ) : b.master_type === "VISUAL_STORY" ? (
              <VisualStoryRender key={b.id} block={b} mode={mode} />
            ) : b.master_type === "RECIPE" ? (
              <RecipeRender key={b.id} block={b} mode={mode} />
            ) : b.master_type === "HISTORICAL_EDITORIAL" ? (
              <HistoricalEditorialRender key={b.id} block={b} mode={mode} />
            ) : b.master_type === "MACRO_SENSORY" ? (
              <MacroSensoryRender key={b.id} block={b} mode={mode} />
            ) : b.master_type === "PORTRAIT_DIALOGUE" ? (
              <PortraitDialogueRender key={b.id} block={b} mode={mode} />
            ) : b.master_type === "MACRO" ? (
              <MacroRender key={b.id} block={b} mode={mode} />
            ) : (
              <section key={b.id} className="px-8 py-8"
                data-testid={`editorial-preview-block-${b.id}`}>
                <div className="text-[9px] uppercase tracking-widest opacity-40 mb-2">
                  {masterLabel(b.master_type)}
                </div>
                {b.content?.eyebrow && (
                  <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">{b.content.eyebrow}</div>
                )}
                {b.content?.headline && (
                  <h2 className="font-serif text-2xl md:text-3xl text-espresso mb-3">{b.content.headline}</h2>
                )}
                {b.content?.lead && (
                  <p className="italic opacity-80 mb-3">{b.content.lead}</p>
                )}
                {b.content?.body && (
                  <div className="text-[15px] leading-relaxed whitespace-pre-wrap">{b.content.body}</div>
                )}
                {b.content?.quote && (
                  <blockquote className="border-l-2 border-espresso/30 pl-4 italic mt-4 font-serif text-xl">
                    {b.content.quote}
                  </blockquote>
                )}
                {b.content?.caption && (
                  <div className="text-xs italic opacity-60 mt-2">{b.content.caption}</div>
                )}
              </section>
            )
          ))}
        </div>
      </div>
    </div>
  );
}
